<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="py-5 custom-container container tradie-profile-view">
        <b-container class="custom-container mt-5">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 pt-5 mt-5 bg-transparent"
          ></b-breadcrumb>

          <!-- ROW -->
          <b-row class="profile-header-row align-items-center">
            <b-col>
              <h2 class="font-weight-bold mb-md-0">
                {{
                  tradie ? (tradie.full_name ? tradie.full_name : '-----') : '-'
                }}
              </h2>
            </b-col>

            <b-col cols="auto" class="text-right align-right">
              <b-button
                variant="outline-secondary"
                class="bg-white text-dark"
                :style="'max-height: 64px'"
                @click="gotoProject()"
              >
                Back to Project
              </b-button>
            </b-col>
          </b-row>

          <!-- Tradie has asked to delete their account (notice period) -->
          <b-alert
            v-if="accountDeletionDate"
            show
            variant="danger"
            class="mt-3"
            data-testid="tradie-account-deletion-notice"
          >
            This tradie is deleting their account and all their uploaded files.
            It will be removed on <strong>{{ accountDeletionDate }}</strong
            >. Keep a copy of anything you are required or permitted to retain.
          </b-alert>

          <!-- ROW - Details -->
          <b-row v-if="tradie">
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <div
                    class="d-flex align-items-start mt-4 profile-detail-flex"
                    style="gap: 32px"
                  >
                    <!-- User Avatar -->
                    <image-cropper
                      v-model="showImageCropper"
                      @change="uploadProfilePhoto"
                      @close="showImageCropper = false"
                      subtitle="Upload Tradie's Profile Photo"
                      loadingMessage="Uploading Image"
                    />
                    <div
                      class="upload-container mb-4"
                      :class="tradie && tradie.avatar ? 'border-0' : ''"
                      v-cloak
                      @drop.prevent="onFileChange"
                      @dragover.prevent
                    >
                      <b-avatar
                        :src="
                          tradie.avatar
                            ? tradie.avatar.thumb_url || tradie.avatar.url
                            : null
                        "
                        button
                        @click="showImageCropper = true"
                        class="ml-0 mr-auto"
                        style="width: 180px; height: 240px"
                        rounded
                        badge
                        badge-top
                        badge-offset="0.5rem"
                        badge-variant="transparent"
                      >
                        <template #badge>
                          <b-icon
                            icon="pencil-square"
                            scale="1.5"
                            class="text-light"
                          />
                        </template>
                      </b-avatar>
                    </div>

                    <b-row no-gutters class="profile-details-grid">
                      <!-- Profile -->
                      <b-col cols="12" class="mb-0">
                        <h4>Profile:</h4>
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Name:</span>
                        {{ tradie.full_name }}
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Email:</span>
                        {{ tradie.email }}
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Trade:</span>
                        {{ tradie.trade }}
                      </b-col>
                      <template v-if="tradie.consent_given">
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Date of Birth:</span>
                          {{ dateFormat(tradie.details.birthdate) }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Gender:</span>
                          {{ tradie.details.gender || 'Prefer not to say' }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Phone Number:</span>
                          {{
                            formatPhoneWithDialCode(tradie.details.phone_number)
                          }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">USI:</span>
                          {{ tradie.details.usi }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Trainee? </span>
                          {{ tradie.details.is_trainee ? 'Yes' : 'No' }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Aboriginal? </span>
                          {{ tradie.details.is_aboriginal ? 'Yes' : 'No' }}
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">Address:</span><br />
                          {{ tradie.details.address }}
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">
                            Medical Conditions:
                          </span>
                          <br />
                          {{ tradie.details.medical_conditions }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">PPE Size:</span><br />
                          {{ tradie.details.ppe_size || '-' }}
                        </b-col>

                        <!-- Emergency Contact -->
                        <b-col cols="12" class="mt-2 mb-0">
                          <h4>Emergency Contact:</h4>
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">Name:</span>
                          {{
                            tradie.emergency_contact.emergency_contact_full_name
                          }}
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">Email:</span>
                          {{ tradie.emergency_contact.emergency_contact_email }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Phone Number:</span>
                          {{
                            formatPhoneWithDialCode(
                              tradie.emergency_contact
                                .emergency_contact_phone_number,
                            )
                          }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Relationship:</span>
                          {{
                            tradie.emergency_contact
                              .emergency_contact_relationship
                          }}
                        </b-col>
                      </template>
                      <b-col v-else cols="12">
                        <div class="consent-hidden-banner">
                          <b-icon-shield-lock
                            class="consent-hidden-banner__icon"
                          />
                          <div>
                            <p class="mb-1">
                              Personal details hidden until the tradie accepts
                              the privacy consent.
                            </p>
                            <p class="mb-0">
                              Please contact the worker to accept the privacy
                              consent through the app.
                            </p>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-container>
              </b-card>
            </b-container>
          </b-row>

          <!-- ROW - Trades -->
          <b-row>
            <b-col cols="12" sm="12" class="d-flex flex-column mb-0">
              <b-container fluid>
                <b-card class="mt-4 file-details">
                  <b-container fluid>
                    <b-row
                      align-v="center"
                      no-gutters
                      class="section-header-row trades-section-row"
                    >
                      <b-col cols="auto">
                        <h3 class="font-weight-bold">Trades:</h3>
                      </b-col>
                      <b-col class="px-4">
                        <b-form-tags
                          input-id="tags-basic"
                          v-model="tradeNames"
                          tag-variant="primary"
                          size="lg"
                          :placeholder="
                            canManageTradieTrades
                              ? 'Click on the add button to add Trade'
                              : ''
                          "
                          no-add-on-enter
                          :limit="tradeNames.length"
                          limit-tags-text=""
                          :disabled="!canManageTradieTrades"
                          @focus="(elem) => elem.target.blur()"
                        >
                          <template v-slot="{ tagVariant }">
                            <span
                              v-if="!tradeChips.length"
                              class="text-muted trades-empty"
                              >No trades assigned yet</span
                            >
                            <b-form-tag
                              v-for="chip in tradeChips"
                              :key="chip.id"
                              :title="
                                canManageTradieTrades
                                  ? `Remove Trade: ${chip.name}`
                                  : chip.name
                              "
                              :variant="tagVariant"
                              :disabled="!canManageTradieTrades"
                              class="mr-2"
                              pill
                              @remove="removeTrade(chip.id)"
                              >{{ chip.name }}</b-form-tag
                            >
                          </template>
                        </b-form-tags>
                      </b-col>
                      <b-col v-if="canManageTradieTrades" cols="auto">
                        <b-button
                          size="lg"
                          variant="primary"
                          class="trades-add-btn"
                          @click="selectingTrades(true)"
                        >
                          <b-icon
                            icon="plus-lg"
                            aria-label="Add Trade"
                            pill
                          ></b-icon>
                        </b-button>
                      </b-col>
                    </b-row>
                  </b-container>
                </b-card>
              </b-container>
            </b-col>

            <!-- Modal - Selecting Trades -->
            <b-modal
              v-model="isSelectingTrades"
              title="Select Trades"
              centered
              :hide-footer="true"
              size="md"
            >
              <div v-if="isLoadingTrades" class="text-center py-5">
                <digger-loader />
              </div>
              <b-row v-if="!isLoadingTrades">
                <b-col>
                  <b-form-input
                    v-model.lazy="searchQuery"
                    placeholder="Type to search trades"
                    @input="onSearchQueryChange"
                    class="mb-4"
                    autofocus
                    clearable
                /></b-col>
              </b-row>
              <b-form-row
                v-if="!isLoadingTrades"
                class="overflow-auto"
                style="height: 480px"
              >
                <b-col>
                  <div v-for="(trade, i) in trades" :key="i">
                    <!-- prettier-ignore -->
                    <div
                      v-if="searchQuery === '' || trade.name.toLowerCase().includes(searchQuery.toLowerCase()) || selectedTradeIds.includes(`${trade.company_id}-${trade.company_id != null ? trade.trade_id : trade.id}`)"
                      class="d-flex flex-row align-items-center mb-2">
                      <div>
                        <b-form-checkbox v-model="selectedTradeIds"
                          :value="`${trade.company_id}-${trade.company_id != null ? trade.trade_id : trade.id}`"
                          inline />
                      </div>
                      <div>{{ trade.name }}</div>
                    </div>
                  </div>
                </b-col>
              </b-form-row>

              <div class="p-2 trades-modal-footer">
                <b-button
                  variant="outline-secondary"
                  @click="selectingTrades(false)"
                >
                  CANCEL
                </b-button>
                <b-button variant="primary" @click="onAddTrades"
                  >SUBMIT</b-button
                >
              </div>
            </b-modal>
          </b-row>

          <!-- ROW - Files -->
          <b-row>
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <b-row
                    align-v="center"
                    align-h="between"
                    no-gutters
                    class="section-header-row"
                  >
                    <b-col cols="auto">
                      <h3 class="font-weight-bold">Files:</h3>
                    </b-col>
                    <b-col v-if="canCreateTradieFile" cols="auto">
                      <b-button
                        variant="primary"
                        class="pl-2 pr-3"
                        :to="{ name: 'templates.list-page' }"
                        style="display: table-cell"
                      >
                        <b-icon-plus
                          font-scale="1.5"
                          class="align-middle"
                        ></b-icon-plus>
                        <span class="align-middle">New File</span>
                      </b-button>
                    </b-col>
                  </b-row>
                  <b-row v-if="canViewTradieFiles">
                    <b-col cols="12">
                      <div class="tradie-files-notice">
                        <span
                          class="tradie-files-notice__icon"
                          aria-hidden="true"
                        >
                          <b-icon-exclamation-triangle-fill />
                        </span>
                        <div class="tradie-files-notice__body">
                          <span class="tradie-files-notice__title d-block mb-2">
                            Important Update on Uploading Files
                          </span>
                          <p class="mb-2">
                            <strong>Private:</strong>
                            Only visible to your company and not shared with the
                            Tradie or other linked companies. For company-only
                            documents, use Private or upload them via the
                            <strong>Company Files</strong>
                            section.
                          </p>
                          <p class="mb-0">
                            <strong>Public:</strong>
                            Visible to the Tradie and may be visible to other
                            companies linked to that Tradie.
                          </p>
                        </div>
                      </div>
                    </b-col>
                  </b-row>

                  <p v-if="!canViewTradieFiles" class="text-muted py-3 mb-0">
                    You don't have permission to view this tradie's files.
                  </p>
                  <template v-else>
                    <b-nav tabs class="tradie-files-tabs mt-3">
                      <b-nav-item
                        :active="filesTab === 'active'"
                        @click="filesTab = 'active'"
                      >
                        Files
                        <b-badge pill variant="light" class="ml-1">{{
                          activeFiles.length
                        }}</b-badge>
                      </b-nav-item>
                      <b-nav-item
                        :active="filesTab === 'archived'"
                        @click="filesTab = 'archived'"
                      >
                        Archived
                        <b-badge pill variant="light" class="ml-1">{{
                          archivedFiles.length
                        }}</b-badge>
                      </b-nav-item>
                    </b-nav>

                    <!-- Mobile: stacked file cards (hidden on md+) -->
                    <div class="d-md-none mobile-file-list">
                      <div
                        v-for="item in displayedFiles"
                        :key="item.id"
                        class="mobile-file-item"
                        :class="mobileFileAttentionClass(item)"
                        @click="viewFile(item)"
                      >
                        <div class="d-flex align-items-start">
                          <div class="flex-grow-1" style="min-width: 0">
                            <div class="mobile-file__name">
                              {{ item.name || '-' }}
                            </div>
                            <div class="mobile-file__meta">
                              <span>
                                <span class="mobile-file__label">Issued</span>
                                {{ item.date_issued || '-' }}
                              </span>
                              <span>
                                <span class="mobile-file__label">Expiry</span>
                                {{ item.date_expiry || '-' }}
                              </span>
                              <span>
                                <span class="mobile-file__label">
                                  Visibility
                                </span>
                                {{ formatFileVisibilityDisplay(item) }}
                              </span>
                              <span class="mobile-file__approval" @click.stop>
                                <span class="mobile-file__label">
                                  Approval
                                </span>
                                <company-approval-badge
                                  :status="item && item.company_approval"
                                  :reason="
                                    (item && item.company_approval_reason) || ''
                                  "
                                  :set-date="
                                    (item && item.company_approval_at) || ''
                                  "
                                  :can-edit="canManageApproval"
                                  :busy="approvalBusyId === (item && item.id)"
                                  @approve="onOpenApprove(item)"
                                  @reject="onOpenReject(item)"
                                  @pending="onOpenPending(item)"
                                  @not-applicable="onOpenNotApplicable(item)"
                                />
                              </span>
                            </div>
                          </div>
                          <div
                            class="mobile-file__status"
                            v-b-tooltip.hover="getStatusCellTooltip(item)"
                          >
                            <b-badge
                              v-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expired'
                              "
                              pill
                              variant="danger"
                              class="tradie-file-status-badge"
                              >Expired</b-badge
                            >
                            <b-badge
                              v-else-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expiring_soon'
                              "
                              pill
                              variant="warning"
                              class="tradie-file-status-badge tradie-file-status-badge--soon"
                              >Expiring soon</b-badge
                            >
                            <span v-else class="mobile-file__status-text">{{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="!displayedFiles || !displayedFiles.length"
                        class="text-center py-4 text-muted"
                      >
                        {{
                          filesTab === 'archived'
                            ? 'No archived files.'
                            : 'There are no files to show.'
                        }}
                      </div>
                    </div>

                    <!-- Desktop: full table (hidden below md) -->
                    <div class="d-none d-md-block">
                      <b-table
                        id="my-table"
                        responsive
                        hover
                        show-empty
                        :empty-text="
                          filesTab === 'archived'
                            ? 'No archived files.'
                            : 'There are no files to show.'
                        "
                        :fields="tradieFilesTableFields"
                        :items="displayedFiles"
                        :tbody-tr-class="profileFileAttentionRowClass"
                        @row-clicked="viewFile"
                      >
                        <template #table-busy>
                          <div class="text-center text-primary my-2">
                            <digger-loader />
                          </div>
                        </template>

                        <template #cell(status)="{ item }">
                          <span
                            class="tradie-file-status-cell"
                            v-b-tooltip.hover="getStatusCellTooltip(item)"
                          >
                            <b-badge
                              v-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expired'
                              "
                              pill
                              variant="danger"
                              class="tradie-file-status-badge"
                              >Expired</b-badge
                            >
                            <b-badge
                              v-else-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expiring_soon'
                              "
                              pill
                              variant="warning"
                              class="tradie-file-status-badge tradie-file-status-badge--soon"
                              >Expiring soon</b-badge
                            >
                            <template v-else>{{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}</template>
                          </span>
                        </template>

                        <template #cell(company_approval)="{ item }">
                          <company-approval-badge
                            :status="item && item.company_approval"
                            :reason="
                              (item && item.company_approval_reason) || ''
                            "
                            :set-date="(item && item.company_approval_at) || ''"
                            :can-edit="canManageApproval"
                            :busy="approvalBusyId === (item && item.id)"
                            @approve="onOpenApprove(item)"
                            @reject="onOpenReject(item)"
                            @pending="onOpenPending(item)"
                            @not-applicable="onOpenNotApplicable(item)"
                          />
                        </template>
                      </b-table>
                    </div>
                  </template>
                </b-container>
              </b-card>
            </b-container>

            <!-- Modal - View File -->
            <ViewFileModal
              :showModal="isViewingFile"
              :fileId="viewFileId"
              :read-only="isSupervisorTradie"
              :project-id="projectId"
              permissionPrefix="projects.tradie.files"
              @close="onViewFileClose"
              @error="onViewFileError"
              @deleted="onViewFileDeleted"
            />

            <RejectFileModal
              :showModal="isRejectingFile"
              :fileName="rejectTargetName"
              @close="onCloseReject"
              @confirm="onConfirmReject"
            />

            <ApproveFileModal
              :showModal="isApprovingFile"
              :fileName="approveTargetName"
              @close="onCloseApprove"
              @confirm="onConfirmApprove"
            />

            <PendingFileModal
              :showModal="isPendingFileOpen"
              :fileName="pendingTargetName"
              @close="onClosePending"
              @confirm="onConfirmPending"
            />

            <NotApplicableFileModal
              :showModal="isNotApplicableFileOpen"
              :fileName="notApplicableTargetName"
              @close="onCloseNotApplicable"
              @confirm="onConfirmNotApplicable"
            />
          </b-row>

          <!-- ROW - Required Files (by Trades) -->
          <b-row>
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <b-row
                    align-v="center"
                    align-h="between"
                    no-gutters
                    class="section-header-row"
                  >
                    <b-col cols="auto">
                      <h3 class="font-weight-bold">
                        Required Files: (by Trades)
                      </h3>
                    </b-col>
                  </b-row>

                  <p v-if="!canViewTradieFiles" class="text-muted py-3 mb-0">
                    You don't have permission to view this tradie's files.
                  </p>
                  <template v-else>
                    <!-- Mobile: stacked required-file cards (hidden on md+) -->
                    <div class="d-md-none mobile-file-list mt-3">
                      <div
                        v-for="item in requiredFiles"
                        :key="item.id"
                        class="mobile-file-item"
                        :class="mobileFileAttentionClass(item)"
                      >
                        <div class="d-flex align-items-start">
                          <div class="flex-grow-1" style="min-width: 0">
                            <div class="mobile-file__name">
                              {{ item.name || '-' }}
                            </div>
                            <div class="mobile-file__meta">
                              <span>
                                <span class="mobile-file__label">Issued</span>
                                {{ item.date_issued || '-' }}
                              </span>
                              <span>
                                <span class="mobile-file__label">Expiry</span>
                                {{ item.date_expiry || '-' }}
                              </span>
                            </div>
                          </div>
                          <div
                            class="mobile-file__status"
                            v-b-tooltip.hover="getStatusCellTooltip(item)"
                          >
                            <b-badge
                              v-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expired'
                              "
                              pill
                              variant="danger"
                              class="tradie-file-status-badge"
                              >Expired</b-badge
                            >
                            <b-badge
                              v-else-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expiring_soon'
                              "
                              pill
                              variant="warning"
                              class="tradie-file-status-badge tradie-file-status-badge--soon"
                              >Expiring soon</b-badge
                            >
                            <span v-else class="mobile-file__status-text">{{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="!requiredFiles || !requiredFiles.length"
                        class="text-center py-4 text-muted"
                      >
                        No required files.
                      </div>
                    </div>

                    <!-- Desktop: full table (hidden below md) -->
                    <div class="d-none d-md-block">
                      <b-table
                        id="required-files-table"
                        class="mt-4"
                        responsive
                        hover
                        show-empty
                        :fields="requiredFilesTableFields"
                        :items="requiredFiles"
                        :tbody-tr-class="profileFileAttentionRowClass"
                      >
                        <template #table-busy>
                          <div class="text-center text-primary my-2">
                            <digger-loader />
                          </div>
                        </template>

                        <template #cell(status)="{ item }">
                          <span
                            class="tradie-file-status-cell"
                            v-b-tooltip.hover="getStatusCellTooltip(item)"
                          >
                            <b-badge
                              v-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expired'
                              "
                              pill
                              variant="danger"
                              class="tradie-file-status-badge"
                              >Expired</b-badge
                            >
                            <b-badge
                              v-else-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expiring_soon'
                              "
                              pill
                              variant="warning"
                              class="tradie-file-status-badge tradie-file-status-badge--soon"
                              >Expiring soon</b-badge
                            >
                            <template v-else>{{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}</template>
                          </span>
                        </template>
                      </b-table>
                    </div>
                  </template>
                </b-container>
              </b-card>
            </b-container>
          </b-row>

          <!-- ROW - Requested Files -->
          <b-row>
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <b-row
                    align-v="center"
                    align-h="between"
                    no-gutters
                    class="section-header-row"
                  >
                    <b-col cols="auto">
                      <h3 class="font-weight-bold">Requested Files:</h3>
                    </b-col>
                    <b-col v-if="canCreateTradieFile" cols="auto">
                      <b-button
                        variant="primary"
                        class="pl-2 pr-3"
                        @click="selectingTemplate(true)"
                      >
                        <b-icon-plus
                          font-scale="1.5"
                          class="align-middle"
                        ></b-icon-plus>
                        <span class="align-middle">Request New File</span>
                      </b-button>
                    </b-col>
                  </b-row>
                  <p v-if="!canViewTradieFiles" class="text-muted py-3 mb-0">
                    You don't have permission to view this tradie's files.
                  </p>
                  <project-tradie-requested-file-list
                    v-else
                    :requests="requestedFiles"
                    @loading="isLoading"
                    @onRequestDeleted="onFileRequestDeleted"
                    class="mt-4"
                  />
                </b-container>
              </b-card>
            </b-container>
          </b-row>
        </b-container>

        <select-template-modal
          :showModal="isSelectingTemplate"
          @closeModal="isSelectingTemplate = false"
          @selected="onTemplateSelected"
        />

        <tradie-request-file-modal
          v-if="tradie"
          :emails="[tradie.email]"
          :showModal="isRequestingFile"
          :template="requestFileTemplate"
          :project-name="project && project.name"
          @sendRequestResponse="onFileRequested"
          @closeModal="requestingFile(false)"
        />

        <existing-file-modal
          :show-modal="showExistingFileModal"
          :files="existingFiles"
          subject="This tradie already has"
          :template-label="requestFileTemplate.label || 'file'"
          show-company-approval
          view-only
          @close="closeExistingFileModal"
          @createNew="onRequestAnotherFile"
          @view="onViewExistingFile"
        />

        <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          :class="{
            'bg-danger border-danger': alertType === 'danger',
          }"
          dismissible
          fade
          :variant="alertType"
          @dismiss-count-down="countDownChanged"
        >
          {{ alertMsg }}
        </b-alert>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProjectTradieRequestedFileList from '@/components/lists/ProjectTradieRequestedFileList'
import SelectTemplateModal from '@/components/modals/SelectTemplateModal'
import TradieRequestFileModal from '@/components/modals/TradieRequestFileModal'
import ExistingFileModal from '@/components/modals/ExistingFileModal'
import { matchFilesForTemplate } from '@/utils/fileMatch'
import ViewFileModal from '@/components/modals/ViewFileModal.vue'
import RejectFileModal from '@/components/modals/RejectFileModal.vue'
import ApproveFileModal from '@/components/modals/ApproveFileModal.vue'
import PendingFileModal from '@/components/modals/PendingFileModal.vue'
import NotApplicableFileModal from '@/components/modals/NotApplicableFileModal.vue'
import CompanyApprovalBadge from '@/components/badges/CompanyApprovalBadge.vue'
import ImageCropper from '@/components/modals/ImageCropper'
import { formatFileVisibilityDisplay } from '@/utils/fileLabels'
import {
  formatTradieFileStatusColumnDisplay,
  formatTradieFilesStatusDisplay,
  getTradieProfileFileAttentionLevel,
  isTradieFileMarkedNa,
  formatStatusSetDate,
} from '@/utils/fileAttention'
import { formatPhoneWithDialCode } from '@/utils/phonePolicy'
import { parseServerDate } from '@/utils/dateHelper'
import { clearTradieUploadContextUnlessInFlow } from '@/utils/tradieUploadContext'

export default {
  components: {
    ProjectTradieRequestedFileList,
    SelectTemplateModal,
    TradieRequestFileModal,
    ExistingFileModal,
    ViewFileModal,
    RejectFileModal,
    ApproveFileModal,
    PendingFileModal,
    NotApplicableFileModal,
    CompanyApprovalBadge,
    ImageCropper,
  },

  data() {
    return {
      // Ids
      projectId: this.$route.params.id,
      tradieId: this.$route.params.tradieId,

      // Status Booleans
      isLoading: false,
      isSelectingTrades: false,
      isLoadingTrades: false,
      isSelectingTemplate: false,
      isRequestingFile: false,
      showExistingFileModal: false,
      existingFiles: [],
      isViewingFile: false,
      showImageCropper: false,

      // Files tabs: 'active' (default) vs 'archived'
      filesTab: 'active',

      // Variables
      searchQuery: '',
      selectedTradeIds: [],
      requestFileTemplate: {
        id: null,
        name: '',
      },
      viewFileId: 0,
      dismissCountDown: 0,

      alertType: 'primary',
      alertMsg: '',

      isRejectingFile: false,
      rejectTarget: null,
      isApprovingFile: false,
      approveTarget: null,
      isPendingFileOpen: false,
      pendingTarget: null,
      isNotApplicableFileOpen: false,
      notApplicableTarget: null,
      approvalBusyId: null,
    }
  },

  computed: {
    // Set while the tradie is inside their account-deletion notice period.
    accountDeletionDate() {
      const at = this.tradie && this.tradie.deletion_scheduled_at
      const d = at ? parseServerDate(at) : null
      return d
        ? d.toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : ''
    },
    ...mapGetters('project', [
      'project',
      'tradie',
      'files',
      'requiredFiles',
      'requestedFiles',
    ]),
    ...mapGetters('trade', ['trades']),

    /** Files whose company approval is Archived. */
    archivedFiles() {
      return (this.files || []).filter(
        (f) => f && f.company_approval === 'not_applicable',
      )
    },
    /** Files not archived — shown under the default "Files" tab. */
    activeFiles() {
      return (this.files || []).filter(
        (f) => !f || f.company_approval !== 'not_applicable',
      )
    },
    /** Rows for the currently selected files tab. */
    displayedFiles() {
      return this.filesTab === 'archived'
        ? this.archivedFiles
        : this.activeFiles
    },

    breadcrumbs() {
      return [
        {
          text: 'Projects',
          to: '/projects',
        },
        {
          text: this.project ? this.project.name : '',
          to: `/projects/${this.projectId}`,
        },
        {
          text: this.tradie ? this.tradie.full_name : '',
        },
      ]
    },

    /**
     * The tradie's trades on this project as { id, name } pairs. `id` is the
     * exact string from `tradie.trade_ids` — i.e. what the remove endpoint
     * expects — so removal never has to reverse-map a name back to an id
     * (which broke when the trades list wasn't loaded, when a trade had been
     * deleted/renamed leaving trade_names shorter than trade_ids, or when two
     * trades shared a name). Name is resolved from the loaded trades list when
     * available, else positionally from `trade_names`.
     */
    tradeChips() {
      if (!this.tradie) return []
      const ids = this.tradie.trade_ids || []
      const names = this.tradie.trade_names || []
      const idOf = (x) =>
        `${x.company_id}-${x.company_id != null ? x.trade_id : x.id}`
      return ids.map((id, i) => {
        let name = null
        if (this.trades && this.trades.length) {
          const t = this.trades.find((x) => idOf(x) === id)
          if (t) name = t.name
        }
        return { id, name: name || names[i] || 'Unknown trade' }
      })
    },

    tradeNames: {
      get() {
        return this.tradeChips.map((c) => c.name)
      },
      set() {
        // No-op: removal is handled by removeTrade() and fetchTradie() updates state
      },
    },

    /** Uploaded files on the tradie's profile — includes visibility + company approval. */
    tradieFilesTableFields() {
      return [
        {
          key: 'name',
          label: 'File Name',
          class: 'w-25',
          sortable: true,
        },
        {
          key: 'date_issued',
          label: 'Issued Date',
          sortable: true,
        },
        {
          key: 'date_expiry',
          label: 'Expiry Date',
          sortable: true,
        },
        {
          key: 'visibility',
          label: 'Visibility',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          formatter: (_value, _key, item) => formatFileVisibilityDisplay(item),
        },
        {
          key: 'status',
          label: 'Tradie Pass Verification',
          sortable: true,
          thClass: 'text-nowrap',
        },
        {
          key: 'company_approval',
          label: 'Company Approval',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'company-approval-cell',
        },
      ]
    },
    /** Required-by-trade rows — no per-file visibility or company approval. */
    requiredFilesTableFields() {
      return [
        {
          key: 'name',
          label: 'File Name',
          class: 'w-25',
          sortable: true,
        },
        {
          key: 'date_issued',
          label: 'Issued Date',
          sortable: true,
        },
        {
          key: 'date_expiry',
          label: 'Expiry Date',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Tradie Pass Verification',
          sortable: true,
          thClass: 'text-nowrap',
        },
      ]
    },

    ...mapGetters('auth', ['user', 'hasPermission']),

    isBusinessAdmin() {
      return !!(this.user && this.user.user_type === 'business_admin')
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    canViewTradieFiles() {
      if (this.isSupervisorTradie) return true
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.tradie.files.view')
    },

    canCreateTradieFile() {
      if (!this.isBusinessAdmin && !this.isSupervisorTradie) return false
      // No new or requested files for a tradie in their account-deletion window.
      if (this.tradie && this.tradie.deletion_scheduled_at) return false
      return this.hasPermission('projects.tradie.files.create')
    },

    canManageApproval() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.tradie.files.approve')
    },

    canManageTradieTrades() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.tradie.trades.manage')
    },

    rejectTargetName() {
      return this.rejectTarget ? this.rejectTarget.name || '' : ''
    },

    approveTargetName() {
      return this.approveTarget ? this.approveTarget.name || '' : ''
    },

    pendingTargetName() {
      return this.pendingTarget ? this.pendingTarget.name || '' : ''
    },

    notApplicableTargetName() {
      return this.notApplicableTarget ? this.notApplicableTarget.name || '' : ''
    },
  },

  beforeRouteLeave(to, from, next) {
    clearTradieUploadContextUnlessInFlow(to)
    next()
  },

  methods: {
    formatTradieFileStatusColumnDisplay,
    formatTradieFilesStatusDisplay,
    getTradieProfileFileAttentionLevel,
    isTradieFileMarkedNa,
    formatFileVisibilityDisplay,
    formatPhoneWithDialCode,

    profileFileAttentionRowClass(item, type) {
      if (!item || type !== 'row') return 'cursor-pointer'
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired')
        return 'cursor-pointer bg-alert tradie-file-row-attention'
      if (level === 'expiring_soon')
        return 'cursor-pointer bg-highlight tradie-file-row-attention'
      return 'cursor-pointer'
    },

    mobileFileAttentionClass(item) {
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') return 'mobile-file-item--expired'
      if (level === 'expiring_soon') return 'mobile-file-item--soon'
      return ''
    },

    ...mapActions('project', {
      getProject: 'getProject',
      getTradie: 'getTradie',
      removeTradeFromTradie: 'removeTradeFromTradie',
      addTradesToTradie: 'addTradesToTradie',
    }),

    ...mapActions('companyFile', {
      setCompanyApproval: 'setCompanyApproval',
    }),

    applyApprovalToLocalFile(updated) {
      if (!updated) return
      this.$store.commit('project/updateFileCompanyApproval', updated)
    },

    handleApprovalError(err) {
      const status = err && err.response && err.response.status
      const payload = err && err.response && err.response.data
      let message
      if (status === 403) {
        message = "You don't have permission to approve files for this tradie"
      } else if (status === 404) {
        message = 'File not found.'
      } else {
        message =
          (payload && payload.message) ||
          (err && err.message) ||
          'Could not update approval.'
      }
      this.showToast('', message, 'danger')
    },

    onOpenApprove(file) {
      if (!file || !this.canManageApproval) return
      this.approveTarget = file
      this.isApprovingFile = true
    },

    onCloseApprove() {
      this.isApprovingFile = false
      this.approveTarget = null
    },

    async onConfirmApprove({ reason, done }) {
      if (!this.approveTarget) {
        done(new Error('Missing file'))
        return
      }
      const fileId = this.approveTarget.id
      this.approvalBusyId = fileId
      try {
        const payload = {
          fileId,
          status: 'approved',
          projectId: this.projectId,
        }
        if (reason) payload.reason = reason
        const updated = await this.setCompanyApproval(payload)
        this.applyApprovalToLocalFile(updated)
        this.showToast('', 'File approved.', 'success')
        done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        done(err)
      } finally {
        this.approvalBusyId = null
      }
    },

    onOpenPending(file) {
      if (!file || !this.canManageApproval) return
      this.pendingTarget = file
      this.isPendingFileOpen = true
    },

    onClosePending() {
      this.isPendingFileOpen = false
      this.pendingTarget = null
    },

    async onConfirmPending({ reason, done }) {
      if (!this.pendingTarget) {
        done(new Error('Missing file'))
        return
      }
      const fileId = this.pendingTarget.id
      this.approvalBusyId = fileId
      try {
        const payload = { fileId, status: 'pending', projectId: this.projectId }
        if (reason) payload.reason = reason
        const updated = await this.setCompanyApproval(payload)
        this.applyApprovalToLocalFile(updated)
        this.showToast('Pending', 'File set to pending.', 'success')
        done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        done(err)
      } finally {
        this.approvalBusyId = null
      }
    },

    onOpenNotApplicable(file) {
      if (!file || !this.canManageApproval) return
      this.notApplicableTarget = file
      this.isNotApplicableFileOpen = true
    },

    onCloseNotApplicable() {
      this.isNotApplicableFileOpen = false
      this.notApplicableTarget = null
    },

    async onConfirmNotApplicable({ reason, done }) {
      if (!this.notApplicableTarget) {
        done(new Error('Missing file'))
        return
      }
      const fileId = this.notApplicableTarget.id
      this.approvalBusyId = fileId
      try {
        const payload = {
          fileId,
          status: 'not_applicable',
          projectId: this.projectId,
        }
        if (reason) payload.reason = reason
        const updated = await this.setCompanyApproval(payload)
        this.applyApprovalToLocalFile(updated)
        this.showToast('', 'File archived.', 'success')
        done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        done(err)
      } finally {
        this.approvalBusyId = null
      }
    },

    onOpenReject(file) {
      if (!file || !this.canManageApproval) return
      this.rejectTarget = file
      this.isRejectingFile = true
    },

    onCloseReject() {
      this.isRejectingFile = false
      this.rejectTarget = null
    },

    async onConfirmReject({ reason, done }) {
      if (!this.rejectTarget) {
        done(new Error('Missing file'))
        return
      }
      const fileId = this.rejectTarget.id
      this.approvalBusyId = fileId
      try {
        const updated = await this.setCompanyApproval({
          fileId,
          status: 'rejected',
          reason,
          projectId: this.projectId,
        })
        this.applyApprovalToLocalFile(updated)
        this.showToast('', 'File rejected.', 'success')
        done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        done(err)
      } finally {
        this.approvalBusyId = null
      }
    },

    ...mapActions('trade', {
      getTrades: 'getTrades',
    }),

    ...mapActions({
      changeTradieAvatar: 'auth/changeTradieAvatar',
    }),

    async fetchProject() {
      this.isLoading = true
      try {
        await this.getProject({
          id: this.projectId,
          limit: 1,
          page: 1,
        })
        if (this.canManageTradieTrades || this.hasPermission('trades.view')) {
          await this.getTrades({ paginate: false })
        }
        await this.fetchTradie()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    onFileChange(e) {
      this.showImageCropper = true
      this.$root.$emit('fileChange', e)
    },

    async uploadProfilePhoto(image) {
      let data = new FormData()
      data.append('avatar', image)
      data.append('tradie_id', this.tradieId)

      await this.changeTradieAvatar(data)
        .then((res) => {
          this.showImageCropper = false
          this.fetchTradie()
        })
        .catch((err) => this.handleError(err))
        .finally(() => this.$root.$emit('uploadComplete'))
    },

    async fetchTradie() {
      try {
        await this.getTradie({
          id: this.projectId,
          tradieId: this.tradieId,
        })
        this.formatFiles()
      } catch (err) {
        if (err.response || err.status === 403) {
          this.showToast('', 'Assign a token to view this tradie.', 'danger')
          this.$router.push(`/projects/${this.projectId}`)
        }
      } finally {
        this.isLoading = false
      }
      // .then((res) => this.fetchTrades())
      // .catch((err) => this.handleError(err))
    },

    async fetchTrades() {
      await this.getTrades({
        paginate: false,
      })
        .catch((err) => this.handleError(err))
        .finally(() => (this.isLoading = false))
    },

    onSearchQueryChange() {
      this.sortTrades()
    },

    sortTrades() {
      this.trades.sort((a, b) => (a.name < b.name ? -1 : 1))
      this.trades.sort(
        (a, b) =>
          this.selectedTradeIds.includes(
            `${b.company_id}-${b.company_id != null ? b.trade_id : b.id}`,
          ) -
          this.selectedTradeIds.includes(
            `${a.company_id}-${a.company_id != null ? a.trade_id : a.id}`,
          ),
      )
    },
    async gotoProject() {
      this.$router.push({
        name: 'projects.view-page',
        params: {
          id: this.projectId,
        },
      })
    },

    async removeTrade(tradeId) {
      if (!this.canManageTradieTrades) return

      if (!tradeId) {
        this.showToast('Error', 'Trade not found. Cannot remove.', 'danger')
        return
      }

      this.isLoading = true
      try {
        await this.removeTradeFromTradie({
          id: this.projectId,
          tradieId: this.tradieId,
          tradeId: tradeId,
        })
        this.showToast('Success', 'Trade removed from Tradie!')
        await this.fetchTradie()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    async selectingTrades(status) {
      this.isSelectingTrades = status
      this.isLoadingTrades = status

      if (status) {
        this.searchQuery = ''

        await this.getTrades({
          paginate: false,
        })
          .then((res) => {
            this.selectedTradeIds = this.tradie.trade_ids
            this.sortTrades()
          })
          .catch((err) => this.handleError(err))
          .finally(() => (this.isLoadingTrades = false))
      }
    },

    async onAddTrades() {
      // Generate Map of TradeId which actually still exists (they might not be when Custom Trade been deleted by Business Admin)
      const tradeIdMap = this.trades.map(
        (x) => `${x.company_id}-${x.company_id != null ? x.trade_id : x.id}`,
      )

      this.isLoadingTrades = true

      await this.addTradesToTradie({
        id: this.projectId,
        tradieId: this.tradieId,
        tradeIds: this.selectedTradeIds.filter((x) => tradeIdMap.includes(x)),
      })
        .then((res) => this.sortTrades())
        .catch((err) => this.handleError(err))
        .finally(() => (this.isSelectingTrades = false))
    },

    viewFile(item, i) {
      if (!this.canViewTradieFiles) return
      this.viewFileId = item.id
      this.isViewingFile = true
    },

    onViewFileClose() {
      this.isViewingFile = false
    },

    onViewFileError(err) {
      this.handleError(err)
    },

    onViewFileDeleted(fileId) {
      const i = this.files.findIndex((x) => x.id === fileId)
      this.files.splice(i, 1)
      this.onViewFileClose()
    },

    async selectingTemplate(status) {
      if (status && !this.canCreateTradieFile) return
      this.isSelectingTemplate = status
    },

    async onTemplateSelected(template) {
      this.requestFileTemplate = template

      // `activeFiles` is what this viewer can see of the tradie's files (the
      // tradie's public files plus this company's own, never another
      // business's private ones), minus archived. Warn if one of this type is
      // already there, but let them request another regardless.
      const matches = matchFilesForTemplate(this.activeFiles, template)
      if (matches.length) {
        this.existingFiles = matches
        this.showExistingFileModal = true
        return
      }

      this.requestingFile(true)
    },

    closeExistingFileModal() {
      this.showExistingFileModal = false
      this.existingFiles = []
    },

    onRequestAnotherFile() {
      this.closeExistingFileModal()
      this.requestingFile(true)
    },

    onViewExistingFile(file) {
      this.closeExistingFileModal()
      if (file) this.viewFile(file)
    },

    requestingFile(status) {
      this.isRequestingFile = status
    },

    onFileRequested(res) {
      if (res.success) {
        this.alertMsg = 'File request shared successfully'
        this.dismissCountDown = 15
      }

      this.fetchTradie()
      this.requestingFile(false)
    },

    onFileRequestDeleted(fileRequestId) {
      const i = this.requestedFiles.findIndex(
        (x) => x.file_request_id === fileRequestId,
      )
      if (i !== -1) this.requestedFiles.splice(i, 1)
      // Deleting the request may have waived a trade-derived required file for
      // this tradie (API side), which also clears their needs-attention flag.
      // Refetch so "Required Files: (by Trades)" and the attention state
      // reflect that instead of going stale until a reload.
      this.fetchTradie()
    },

    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },
    getStatusCellTooltip(item) {
      if (!item) return { title: '', disabled: true }
      const statusLabel = this.tradieFileStatusLabel(item)
      if (!statusLabel) return { title: '', disabled: true }
      let reason = ''
      const raw = item.status_reason
      if (raw && raw !== '-') {
        const trimmed = String(raw).trim()
        if (trimmed) reason = trimmed
      }
      const setDate = formatStatusSetDate(item.status_updated_at)
      let title = reason ? `${statusLabel}: ${reason}` : statusLabel
      if (setDate) title += ` - ${setDate}`
      return { title, disabled: false }
    },
    tradieFileStatusLabel(item) {
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') return 'Expired'
      if (level === 'expiring_soon') return 'Expiring soon'
      const raw = formatTradieFilesStatusDisplay(item && item.status)
      if (raw == null || raw === '') return ''
      return String(raw).replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatFileRowReasons(list) {
      ;(list || []).forEach((file) => {
        if (file.status === 'expired') {
          this.$set(file, 'status_reason', '-')
        }
        if (file.status === 'pending approval') {
          this.$set(file, 'status_reason', '')
        }
      })
    },
    formatFiles() {
      this.formatFileRowReasons(this.files)
      this.formatFileRowReasons(this.requiredFiles)
    },
    handleError(err) {
      this.showToast(
        'Oops.. Something went wrong!',
        err.response.data.message ? err.response.data.message : err.message,
        'danger',
      )
    },

    dateFormat(dateString) {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
  },

  async mounted() {
    const needsProject = !this.project || this.project.id !== this.projectId
    const needsTradie = !this.tradie || this.tradie.id !== this.tradieId

    if (needsProject) {
      await this.fetchProject()
    } else if (needsTradie) {
      await this.fetchTradie()
    }

    localStorage.setItem('project_id', this.projectId)
    localStorage.setItem('tradie_id', this.tradieId)
  },
}
</script>

<style lang="scss" scoped>
$targetHeight: 52.5px;

.consent-hidden-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background-color: #fff8ec;
  border: 1px solid #f5dfae;
  border-left: 4px solid #e2a53a;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  color: #7a5a1e;
  font-size: 0.875rem;

  &__icon {
    flex-shrink: 0;
    margin-top: 0.15rem;
    font-size: 1.1rem;
    color: #e2a53a;
  }

  p {
    color: inherit;
  }
}

/* Add-trade (+) button: always a perfect circle. Fixed equal width/height and
 * zero horizontal padding stop it stretching into an oval as the flex row
 * narrows on small screens. */
.trades-add-btn {
  width: 3rem;
  height: 3rem;
  flex: 0 0 auto;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Select-Trades modal footer: keep CANCEL / SUBMIT side-by-side even at 320px.
 * A plain b-row wraps them; the global .btn padding (4px 32px) makes them too
 * wide to fit, so they'd stack. Trim the padding and let them shrink instead. */
.trades-modal-footer {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 0.5rem;

  .btn {
    flex: 0 1 auto;
    min-width: 0;
    padding-left: 1rem;
    padding-right: 1rem;
    white-space: nowrap;
  }
}

/deep/ .b-form-tags-list {
  height: calc(#{$targetHeight} - 16px);
}

/deep/ .b-form-tags-input {
  height: calc(#{$targetHeight} - 16px - 6px);
}

/deep/ .cursor-pointer {
  cursor: pointer;
}

/deep/ .cursor-pointer td {
  vertical-align: middle;
}

.tradie-files-notice {
  position: relative;
  width: 100%;
  margin-top: 1rem;
  padding: 1rem 1.25rem 1rem 2.75rem;
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 8px;
  color: #383d41;

  &__icon {
    position: absolute;
    left: 1.25rem;
    top: 1rem;
    color: #ffc107;
    font-size: 1.1rem;
    line-height: 1;
  }

  &__title {
    font-weight: 700;
    color: #383d41;
    font-size: 0.9375rem;
  }

  &__body {
    line-height: 1.5;
    font-size: 0.95rem;

    strong {
      font-weight: 600;
    }
  }
}

.tradie-file-status-cell {
  display: inline-block;
}

.tradie-file-status-badge {
  font-weight: 600;
}

.tradie-file-status-badge--soon {
  background-color: #fff3cd !important;
  color: #856404 !important;
  border: 1px solid #ffc107;
}

/deep/ tr.tradie-file-row-attention.bg-alert,
/deep/ tr.tradie-file-row-attention.bg-alert:hover {
  background-color: #ffeaea !important;
}

/deep/ tr.tradie-file-row-attention.bg-highlight {
  background-color: #fff8e1 !important;
}

/* ---------------------------------------------------------------------------
 * Mobile file cards — below md the tables are replaced by compact stacked
 * cards (one file per card) so nothing scrolls sideways.
 * ------------------------------------------------------------------------- */
.mobile-file-list {
  border-top: 1px solid #e1e6ea;
}

.mobile-file-item {
  padding: 0.65rem 0.25rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
  cursor: pointer;
}

.mobile-file-item--expired {
  background-color: #ffeaea;
}

.mobile-file-item--soon {
  background-color: #fff8e1;
}

.mobile-file__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #364052;
  line-height: 1.3;
  word-break: break-word;
}

.mobile-file__meta {
  font-size: 0.75rem;
  color: #8b95a3;
  margin-top: 0.2rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
}

.mobile-file__label {
  font-weight: 600;
  color: #6c7581;
  margin-right: 0.15rem;
}

.mobile-file__approval {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.1rem;
}

.mobile-file__status {
  flex-shrink: 0;
  margin-left: 0.5rem;
  text-align: right;
}

.mobile-file__status-text {
  font-size: 0.72rem;
  color: #6c7581;
  text-transform: capitalize;
}

/* ---------------------------------------------------------------------------
 * Small-screen responsiveness (down to 320px).
 *
 * All rules live inside max-width media queries so the desktop/laptop layout
 * is left byte-for-byte identical. The layout gets progressively more compact
 * as the viewport shrinks: spacing tightens first (<=768px), then structural
 * elements stack (<=576px), then sizes shrink further on the smallest phones.
 * ------------------------------------------------------------------------- */

/* Tablet / large phone: trim oversized vertical spacing. */
@media (max-width: 767.98px) {
  .tradie-profile-view {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }

  /deep/ .breadcrumb {
    padding-top: 2rem !important;
    margin-top: 2rem !important;
  }

  .profile-detail-flex {
    gap: 1.5rem !important;
  }
}

/* Phones: stack sections and reclaim padding. */
@media (max-width: 575.98px) {
  .tradie-profile-view {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  /deep/ .breadcrumb {
    padding-top: 1rem !important;
    margin-top: 1rem !important;
    font-size: 0.85rem;
  }

  /* Title + "Back to Project" stack vertically. */
  .profile-header-row {
    flex-direction: column;

    > .col,
    > [class*='col-'] {
      flex: 0 0 100%;
      max-width: 100%;
      text-align: left !important;
    }

    h2 {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }

    .btn {
      max-width: 100% !important;
      margin-top: 0.25rem;
    }
  }

  /* Avatar sits above the profile detail grid. */
  .profile-detail-flex {
    flex-direction: column;
    gap: 1rem !important;
    margin-top: 1rem !important;
  }

  /* Reclaim horizontal space from card padding. */
  /deep/ .file-details > .card-body {
    padding: 1rem;
  }

  /* Profile details: 2-column grid. Long values span full width;
   * short values (dates, toggles, etc.) pair up side-by-side. */
  .profile-details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1rem;
    row-gap: 0.65rem;

    > * {
      grid-column: 1 / -1;
      margin-bottom: 0 !important;
      font-size: 0.85rem;
      line-height: 1.35;
    }

    > .profile-col-half {
      grid-column: span 1;
    }

    .font-weight-bold {
      display: block;
      font-size: 0.66rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #8b95a3;
      margin-bottom: 1px;
    }

    br {
      display: none;
    }

    h4 {
      font-size: 0.95rem;
      margin-top: 0.35rem;
      margin-bottom: 0.15rem;
    }
  }

  /* Section title + action button: slimmer buttons on phones. */
  .section-header-row {
    row-gap: 0.75rem;

    .btn {
      height: auto;
      padding: 0.4rem 0.8rem;
      font-size: 0.85rem;
      border-radius: 6px;

      /deep/ svg,
      .b-icon {
        font-size: 1.1rem;
      }
    }

    /* Keep the round add-trade button square/circular on phones — the generic
     * .btn rule above would otherwise flatten it into a rounded rectangle. */
    .btn.trades-add-btn {
      width: 2.5rem;
      height: 2.5rem;
      padding: 0;
      border-radius: 50%;
    }
  }

  /* Trades section: on very small screens the form-tags column wraps below
   * the heading so it gets full width instead of being squeezed. */
  .trades-section-row {
    flex-wrap: wrap;

    /* Middle column (the form-tags) drops to its own full-width row. */
    > .col {
      order: 3;
      flex: 0 0 100%;
      max-width: 100%;
      padding-left: 0 !important;
      padding-right: 0 !important;
      margin-top: 0.5rem;
    }

    /* "Trades:" heading stays on the left. */
    > .col-auto:first-child {
      order: 1;
      flex: 1 1 auto;
    }

    /* Add-trade (+) button stays on the right of the heading row. */
    > .col-auto:last-child {
      order: 2;
    }
  }

  h3 {
    font-size: 1.25rem;
  }
  h4 {
    font-size: 1.1rem;
  }

  /* Compact the upload-guidance notice. */
  .tradie-files-notice {
    margin-top: 0.75rem;
    padding: 0.75rem 0.85rem 0.75rem 2.15rem;
    border-radius: 6px;

    &__icon {
      left: 0.8rem;
      top: 0.75rem;
      font-size: 0.95rem;
    }

    &__title {
      font-size: 0.85rem;
      margin-bottom: 0.35rem !important;
    }

    &__body {
      font-size: 0.8rem;
      line-height: 1.45;

      p {
        margin-bottom: 0.45rem !important;
      }
    }
  }

  /* Files tabs: on small screens render as a segmented control so the active
   * tab (Files vs Archived) is obvious instead of near-invisible white-on-white. */
  /deep/ .tradie-files-tabs {
    border-bottom: none;
    flex-wrap: nowrap;
    gap: 0.5rem;

    .nav-item {
      flex: 1 1 0;
      text-align: center;
    }

    .nav-link {
      border: 1px solid #d5dbe2;
      border-radius: 8px;
      background-color: #f1f4f8;
      color: #3a4652;
      font-weight: 600;
      padding: 0.5rem 0.6rem;
      white-space: nowrap;

      &:hover {
        border-color: #b9c3ce;
      }

      &.active {
        background-color: #006cff;
        border-color: #006cff;
        color: #fff;
      }

      /* Count badge: readable on both the light (inactive) and blue (active) tab. */
      .badge {
        background-color: #fff;
        color: #3a4652;
      }

      &.active .badge {
        background-color: rgba(255, 255, 255, 0.25);
        color: #fff;
      }
    }
  }
}

/* Smallest phones (<=~360px): shrink the avatar so it never crowds the edges. */
@media (max-width: 359.98px) {
  .profile-detail-flex /deep/ .b-avatar {
    width: 140px !important;
    height: 187px !important;
  }
}
</style>
